import * as THREE from "three";
import { dom } from "../dom";
import { state } from "../state";
import { t } from "../i18n";
import { getActiveFragmentsModel } from "../fragments/engine";

/**
 * Режим X-Ray (полупрозрачная модель) для раздела «Общие данные IFC».
 *
 * Почему так, а не material.wireframe: геометрия Fragments живёт в GPU-буферах,
 * поэтому wireframe построить нельзя. Зато грани рендерятся обычным
 * THREE.MeshLambertMaterial — у него штатно работают opacity/transparent.
 * Поэтому X-Ray делаем через прозрачность материалов меша модели. Это отдельный
 * «канал» от подсветки (highlight), поэтому он не конфликтует с выделением,
 * цветовыми правилами и сравнением.
 *
 * Тайлы геометрии Fragments создаются/удаляются динамически (LOD/куллинг),
 * поэтому пока X-Ray включён, переприменяем прозрачность к новым мешам по
 * событию onViewUpdated.
 */

const XRAY_OPACITY = 0.3;

type MaterialState = { opacity: number; transparent: boolean; depthWrite: boolean };
const originalMaterialState = new WeakMap<THREE.Material, MaterialState>();

let viewUpdatedCb: (() => void) | null = null;

function getModel() {
  if (!state.ifcModel) {
    return null;
  }
  return getActiveFragmentsModel(String(state.ifcModel.modelID)) ?? null;
}

function materialsOf(mesh: THREE.Mesh): THREE.Material[] {
  if (Array.isArray(mesh.material)) {
    return mesh.material;
  }
  return mesh.material ? [mesh.material] : [];
}

function setMaterialXray(mat: THREE.Material, on: boolean): void {
  const m = mat as THREE.Material & {
    opacity: number;
    transparent: boolean;
    depthWrite: boolean;
  };
  if (on) {
    if (!originalMaterialState.has(mat)) {
      originalMaterialState.set(mat, {
        opacity: m.opacity,
        transparent: m.transparent,
        depthWrite: m.depthWrite,
      });
    }
    m.opacity = XRAY_OPACITY;
    m.transparent = true;
    m.depthWrite = false;
  } else {
    const orig = originalMaterialState.get(mat);
    if (orig) {
      m.opacity = orig.opacity;
      m.transparent = orig.transparent;
      m.depthWrite = orig.depthWrite;
      originalMaterialState.delete(mat);
    } else {
      m.opacity = 1;
      m.transparent = false;
      m.depthWrite = true;
    }
  }
  m.needsUpdate = true;
}

function applyToObject(obj: THREE.Object3D, on: boolean): void {
  obj.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!(mesh as unknown as { isMesh?: boolean }).isMesh) {
      return;
    }
    for (const mat of materialsOf(mesh)) {
      setMaterialXray(mat, on);
    }
  });
}

function updateButtonLabel(): void {
  dom.wireframeButton.textContent = state.xrayEnabled
    ? t("btn.wireframeOff")
    : t("btn.wireframe");
}

export function applyXray(enabled: boolean): void {
  state.xrayEnabled = enabled;
  updateButtonLabel();
  const model = getModel();
  if (!model) {
    return;
  }
  applyToObject(model.object, enabled);
  if (enabled) {
    if (!viewUpdatedCb) {
      viewUpdatedCb = () => {
        const current = getModel();
        if (current && state.xrayEnabled) {
          applyToObject(current.object, true);
        }
      };
      try {
        model.onViewUpdated.add(viewUpdatedCb);
      } catch (_) {
        /* ignore */
      }
    }
  } else if (viewUpdatedCb) {
    try {
      model.onViewUpdated.remove(viewUpdatedCb);
    } catch (_) {
      /* ignore */
    }
    viewUpdatedCb = null;
  }
}

export function toggleXray(): void {
  applyXray(!state.xrayEnabled);
}

/**
 * Сброс при загрузке новой модели: старая модель уничтожена (отписываться не от
 * чего), X-Ray возвращаем в выкл и обновляем подпись кнопки.
 */
export function resetXrayForNewModel(): void {
  viewUpdatedCb = null;
  state.xrayEnabled = false;
  updateButtonLabel();
}
